import type { ExtractResource, ResourceType } from '@medplum/fhirtypes'
import { referenceDisplay, getDetailString } from '@seen/core/display'
import { createReference, getReferenceString } from '@seen/core/references'
import { Button } from '@seen/ui'
import { mapValues } from 'lodash-es'
import { type JSX, useReducer } from 'react'
import { ComboboxOption } from '#src/components/ComboboxOption.tsx'
import { Combobox } from '#src/components/core/combobox/Combobox.tsx'
import { ResourceCard } from '#src/components/core/ResourceCard.tsx'
import {
  type UseUserInputSearchParam,
  useUserInputSearch,
} from '#src/hooks/useUserInputSearch.ts'

type SearchParam<T extends ResourceType> = UseUserInputSearchParam<T>

export interface ResourceAutocompleteProps<
  T extends ResourceType = ResourceType,
> {
  searchQuery:
    | ((value: string) => { [K in T]: SearchParam<K> })
    | { [K in T]: SearchParam<K> }
  onOptionSubmit?: (value: ExtractResource<T> | undefined) => any
  clearOnSubmit?: boolean
  placeholder?: string
  name?: string
  required?: boolean
  disabled?: boolean
  value?: string
  onChange?: (value: string) => void
  className?: string
  onBlur?: () => void
}

export function ResourceAutocomplete<T extends ResourceType = ResourceType>(
  props: ResourceAutocompleteProps<T>
): JSX.Element | null {
  const query = mapValues(
    typeof props.searchQuery === 'function'
      ? props.searchQuery(props.value ?? '')
      : props.searchQuery,
    (searchParam) => toUseUserInputSearchParam(searchParam)
  ) as { [K in T]: UseUserInputSearchParam<K> }
  const { resources } = useUserInputSearch(props.value ?? '', query)

  const [_, didSubmit] = useReducer(() => {
    if (props.clearOnSubmit) {
      props.onChange?.('')
    }
  }, undefined)

  const options = resources.map((resource) => ({
    value: getReferenceString(resource),
    label: (
      <ComboboxOption isSelected={props.value === getReferenceString(resource)}>
        <ResourceCard value={resource} />
      </ComboboxOption>
    ),
    keywords: [referenceDisplay(createReference(resource))],
  }))

  const selectedResource = resources.find(
    (r) => getReferenceString(r) === props.value
  )

  return (
    <Combobox
      options={options}
      onSelect={(value) => {
        props.onChange?.(value)
        const resource = resources.find((r) => getReferenceString(r) === value)
        if (props.onOptionSubmit) {
          props.onOptionSubmit(resource)
        }
        didSubmit()
      }}
    >
      <Button
        variant="outline"
        onBlur={props.onBlur}
        name={props.name}
        disabled={props.disabled}
        className={props.className}
      >
        {!!selectedResource && <ResourceCard value={selectedResource} />}
        {!selectedResource && (props.placeholder ?? 'Select…')}
      </Button>
    </Combobox>
  )
}

function toUseUserInputSearchParam<T extends ResourceType = ResourceType>(
  searchParam: SearchParam<T>
): UseUserInputSearchParam<T> {
  return {
    ...searchParam,
    indexers: [...(searchParam.indexers ?? []), { getKey: getDetailString }],
  } as UseUserInputSearchParam<T>
}
